import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  CommonModal,
  CommonInput,
  CommonSelect,
  CommonButton,
  CommonDatePicker,
} from "../Input";
import Swal from "sweetalert2";

const CreateBeneficiaryModal = ({ show, onHide, beneficiary }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (beneficiary) {
      const details = beneficiary.details || {};
      setFormData({
        country: {
          label: details.country || "Singapore",
          value: details.country || "Singapore",
        },
        currency: {
          label: details.currency || "SGD",
          value: details.currency || "SGD",
        },
        receiverType: {
          label: beneficiary.receiverType || "Individual",
          value: beneficiary.receiverType || "Individual",
        },
        receivingMethod: {
          label:
            beneficiary.receivingMethod ||
            details.receivingMethod ||
            "Bank Transfer",
          value:
            beneficiary.receivingMethod ||
            details.receivingMethod ||
            "Bank Transfer",
        },
        relationship: {
          label:
            details.relationship ||
            beneficiary.relationship ||
            "Business partner",
          value:
            details.relationship ||
            beneficiary.relationship ||
            "Business partner",
        },
        idType: {
          label: details.idType || "NRIC",
          value: details.idType || "NRIC",
        },
        idNumber: details.idNumber || "",
        idExpiredDate: details.idExpiredDate || "",
        dob: details.dob || "",
        firstName: details.firstName || beneficiary.name?.split(" ")[0] || "",
        middleName: details.middleName || "",
        middleInitial: details.middleInitial || "",
        lastName:
          details.lastName ||
          beneficiary.name?.split(" ").slice(1).join(" ") ||
          "",
        jointAccountHolder: details.jointAccountHolder || "",
        tin: details.tin || "",
        companyRegNo: details.companyRegNo || "",
        nativeName: details.nativeName || "",
        phonePrefix: { label: "+65", value: "+65" },
        contactNumber: details.contactNumber || beneficiary.contactNo || "",
        address: details.address || "",
        city: details.city || "",
        state: details.state || "",
        postalCode: details.postalCode || "",
        suburb: details.suburb || "",
        bankName: {
          label: details.bankName || beneficiary.bank || "OMNIPAY, INC.",
          value: details.bankName || beneficiary.bank || "OMNIPAY, INC.",
        },
        accountNumber: details.accountNumber || "",
        iban: details.iban || "",
        accountType: {
          label: details.accountType || "Savings",
          value: details.accountType || "Savings",
        },
        bankAccountTitle: details.bankAccountTitle || beneficiary.name || "",
        ifscNumber: details.ifscNumber || "",
        routingNumber: details.routingNumber || "",
        bsbNumber: details.bsbNumber || "",
        branchCode: details.branchCode || "",
        branchName: details.branchName || "",
        transitNumber: details.transitNumber || "",
        sortCode: details.sortCode || "",
        swiftCode: details.swiftCode || "",
        branchNumber: details.branchNumber || "",
        institutionNumber: details.institutionNumber || "",
        cashPickupLocation: details.cashPickupLocation || "",
      });
    } else {
      setFormData({
        country: { label: "Singapore", value: "Singapore" },
        currency: { label: "SGD", value: "SGD" },
        phonePrefix: { label: "+65", value: "+65" },
      });
    }
  }, [beneficiary, show]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    Swal.fire({
      icon: "success",
      title: "Success",
      text: beneficiary ? "Beneficiary updated successfully!" : "Beneficiary created successfully!",
      confirmButtonColor: "var(--primary)",
    }).then(() => {
      onHide();
    });
  };

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      title={
        <div>
          <h4 className="fw-bold text-black">
            {beneficiary ? "Edit Beneficiary" : "Create Beneficiary"}
          </h4>
          <p className="text-muted fs-12 mb-0">
            {beneficiary
              ? "Update beneficiary information"
              : "Create a new beneficiary for this customer"}
          </p>
        </div>
      }
      headerClassName="border-bottom pb-3"
      fullscreen={true}
      bodyClassName="bg-white"
      footer={
        <div className="d-flex justify-content-end gap-3 w-100 px-4">
          <CommonButton
            variant="outline"
            className="px-5 bg-white text-black border-light-gray"
            onClick={onHide}
          >
            Cancel
          </CommonButton>
          <CommonButton className="px-5" onClick={handleSave}>
            Save
          </CommonButton>
        </div>
      }
    >
      <div>
        {/* Basic Information */}
        <section className="mb-4">
          <h5 className="fw-bold text-black mb-0">Basic Information</h5>
          <Row className="g-3">
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Country</label>
              <CommonSelect
                placeholder="Singapore"
                value={formData.country}
                onChange={(val) => handleChange("country", val)}
                options={[
                  { label: "Philippines", value: "Philippines" },
                  { label: "Singapore", value: "Singapore" },
                ]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Currency</label>
              <CommonSelect
                placeholder="SGD"
                value={formData.currency}
                onChange={(val) => handleChange("currency", val)}
                options={[
                  { label: "Philippine Peso", value: "PHP" },
                  { label: "Singapore Dollar", value: "SGD" },
                ]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Receiver type</label>
              <CommonSelect
                placeholder="Individual"
                value={formData.receiverType}
                onChange={(val) => handleChange("receiverType", val)}
                options={[{ label: "Individual", value: "Individual" }]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Receiving method</label>
              <CommonSelect
                placeholder="Bank Transfer"
                value={formData.receivingMethod}
                onChange={(val) => handleChange("receivingMethod", val)}
                options={[{ label: "Bank Transfer", value: "Bank Transfer" }]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Relationship to sender
              </label>
              <CommonSelect
                placeholder="Business partner"
                value={formData.relationship}
                onChange={(val) => handleChange("relationship", val)}
                options={[
                  { label: "Business partner", value: "Business partner" },
                ]}
              />
            </Col>
          </Row>
        </section>

        {/* ID Information */}
        <section className="mb-4">
          <h5 className="fw-bold text-black mb-0">ID Information</h5>
          <Row className="g-3">
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">ID Type</label>
              <CommonSelect
                placeholder="Select ID type"
                value={formData.idType}
                onChange={(val) => handleChange("idType", val)}
                options={[{ label: "NRIC", value: "NRIC" }]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">ID Number</label>
              <CommonInput
                placeholder="Enter ID number"
                value={formData.idNumber}
                onChange={(e) => handleChange("idNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">ID Expiry Date</label>
              <CommonDatePicker
                placeholder="mm / dd / yyyy"
                value={formData.idExpiredDate}
                onChange={(val) => handleChange("idExpiredDate", val)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Date of Birth</label>
              <CommonDatePicker
                placeholder="mm / dd / yyyy"
                value={formData.dob}
                onChange={(val) => handleChange("dob", val)}
              />
            </Col>
          </Row>
        </section>

        {/* Personal Information */}
        <section className="mb-4">
          <h5 className="fw-bold text-black mb-0">Personal Information</h5>
          <Row className="g-3">
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">First Name</label>
              <CommonInput
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Middle Name</label>
              <CommonInput
                placeholder="Enter middle name"
                value={formData.middleName}
                onChange={(e) => handleChange("middleName", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Middle Initial</label>
              <CommonInput
                placeholder="Enter middle initial"
                value={formData.middleInitial}
                onChange={(e) => handleChange("middleInitial", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Last Name</label>
              <CommonInput
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Name of Joint Account Holder
              </label>
              <CommonInput
                placeholder="Enter name of joint account holder"
                value={formData.jointAccountHolder}
                onChange={(e) =>
                  handleChange("jointAccountHolder", e.target.value)
                }
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Tax Identification Number
              </label>
              <CommonInput
                placeholder="Enter tax identification number"
                value={formData.tin}
                onChange={(e) => handleChange("tin", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Company Registration Number
              </label>
              <CommonInput
                placeholder="Enter company registration number"
                value={formData.companyRegNo}
                onChange={(e) => handleChange("companyRegNo", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Native Name</label>
              <CommonInput
                placeholder="Enter native name"
                value={formData.nativeName}
                onChange={(e) => handleChange("nativeName", e.target.value)}
              />
            </Col>
          </Row>
        </section>

        {/* Contact Information */}
        <section className="mb-4">
          <h5 className="fw-bold text-black mb-0">Contact Information</h5>
          <Row className="g-3">
            <Col md={4}>
              <label className="text-muted fs-12 mb-1">Contact Number</label>
              <div className="d-flex gap-2">
                <div style={{ width: "180px" }}>
                  <CommonSelect
                    placeholder="+65"
                    value={formData.phonePrefix}
                    onChange={(val) => handleChange("phonePrefix", val)}
                    options={[{ label: "+65", value: "+65" }]}
                  />
                </div>
                <div className="flex-grow-1">
                  <CommonInput
                    placeholder="Enter contact number"
                    value={formData.contactNumber}
                    onChange={(e) =>
                      handleChange("contactNumber", e.target.value)
                    }
                  />
                </div>
              </div>
            </Col>
          </Row>
        </section>

        {/* Address Information */}
        <section className="mb-4">
          <h5 className="fw-bold text-black mb-0">Address Information</h5>
          <Row className="g-3">
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Address</label>
              <CommonInput
                placeholder="Enter address"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">City</label>
              <CommonInput
                placeholder="Enter city"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">State</label>
              <CommonInput
                placeholder="Enter state"
                value={formData.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Postal Code</label>
              <CommonInput
                placeholder="Enter postal code"
                value={formData.postalCode}
                onChange={(e) => handleChange("postalCode", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Suburb</label>
              <CommonInput
                placeholder="Enter suburb"
                value={formData.suburb}
                onChange={(e) => handleChange("suburb", e.target.value)}
              />
            </Col>
          </Row>
        </section>

        {/* Account Details */}
        <section className="mb-4 text-nowrap">
          <h5 className="fw-bold text-black mb-0">Account Details</h5>
          <Row className="g-3">
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Bank</label>
              <CommonSelect
                placeholder="Select bank"
                value={formData.bankName}
                onChange={(val) => handleChange("bankName", val)}
                options={[{ label: "OMNIPAY, INC.", value: "OMNIPAY, INC." }]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Cash pickup location
              </label>
              <CommonInput
                placeholder="Enter location"
                value={formData.cashPickupLocation}
                onChange={(e) =>
                  handleChange("cashPickupLocation", e.target.value)
                }
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Account Number</label>
              <CommonInput
                placeholder="Enter account number"
                value={formData.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">IBAN Number</label>
              <CommonInput
                placeholder="Enter IBAN number"
                value={formData.iban}
                onChange={(e) => handleChange("iban", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Account Type</label>
              <CommonSelect
                placeholder="Enter account type"
                value={formData.accountType}
                onChange={(val) => handleChange("accountType", val)}
                options={[{ label: "Savings", value: "Savings" }]}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Bank Account Title
              </label>
              <CommonInput
                placeholder="Enter bank account title"
                value={formData.bankAccountTitle}
                onChange={(e) =>
                  handleChange("bankAccountTitle", e.target.value)
                }
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">IFSC Number</label>
              <CommonInput
                placeholder="Enter IFSC number"
                value={formData.ifscNumber}
                onChange={(e) => handleChange("ifscNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Routing Number</label>
              <CommonInput
                placeholder="Enter routing number"
                value={formData.routingNumber}
                onChange={(e) => handleChange("routingNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">BSB Number</label>
              <CommonInput
                placeholder="Enter BSB number"
                value={formData.bsbNumber}
                onChange={(e) => handleChange("bsbNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Branch Code</label>
              <CommonInput
                placeholder="Enter branch code"
                value={formData.branchCode}
                onChange={(e) => handleChange("branchCode", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Branch Name</label>
              <CommonInput
                placeholder="Enter branch name"
                value={formData.branchName}
                onChange={(e) => handleChange("branchName", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Transit Number</label>
              <CommonInput
                placeholder="Enter transit number"
                value={formData.transitNumber}
                onChange={(e) => handleChange("transitNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Sort Code</label>
              <CommonInput
                placeholder="Enter sort code"
                value={formData.sortCode}
                onChange={(e) => handleChange("sortCode", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">SWIFT Code</label>
              <CommonInput
                placeholder="Enter SWIFT code"
                value={formData.swiftCode}
                onChange={(e) => handleChange("swiftCode", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">Branch Number</label>
              <CommonInput
                placeholder="Enter branch number"
                value={formData.branchNumber}
                onChange={(e) => handleChange("branchNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="text-muted fs-12 mb-1">
                Institution Number
              </label>
              <CommonInput
                placeholder="Enter institution number"
                value={formData.institutionNumber}
                onChange={(e) =>
                  handleChange("institutionNumber", e.target.value)
                }
              />
            </Col>
          </Row>
        </section>
      </div>
    </CommonModal>
  );
};

export default CreateBeneficiaryModal;
